import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Microscope, 
  Brain, 
  Cpu, 
  Database, 
  Globe, 
  Zap, 
  Target, 
  TrendingUp,
  ArrowRight,
  Download,
  Calendar,
  Users,
  Award,
  BookOpen,
  Lightbulb,
  Atom,
  Shield,
  Eye,
  Layers,
  Network
} from 'lucide-react';

const ResearchPage = () => {
  const [selectedResearchArea, setSelectedResearchArea] = useState(0);
  const [hoveredPaper, setHoveredPaper] = useState(null);

  const researchAreas = [
    {
      id: 'ai-ml',
      icon: <Brain className="w-12 h-12" />,
      title: 'Artificial Intelligence & Machine Learning',
      description: 'Advancing the frontiers of AI with research in neural networks, deep learning architectures, and novel training methodologies.',
      focus: [
        'Large Language Models (LLMs) optimization',
        'Federated learning for privacy-preserving AI',
        'Explainable AI and interpretability',
        'Multimodal AI systems',
        'Automated machine learning (AutoML)',
        'Reinforcement learning applications'
      ],
      currentProjects: [
        'Next-generation transformer architectures',
        'AI safety and alignment research',
        'Edge AI optimization techniques',
        'Cross-domain transfer learning'
      ],
      publications: 23,
      patents: 8,
      collaborations: ['Stanford AI Lab', 'MIT CSAIL', 'Google Research'],
      color: 'from-blue-400 to-cyan-500'
    },
    {
      id: 'quantum',
      icon: <Atom className="w-12 h-12" />,
      title: 'Quantum Computing Applications',
      description: 'Exploring quantum algorithms for optimization, cryptography, and machine learning to solve previously intractable problems.',
      focus: [
        'Quantum machine learning algorithms',
        'Quantum optimization for logistics',
        'Post-quantum cryptography',
        'Quantum error correction',
        'Hybrid quantum-classical systems',
        'Quantum simulation for drug discovery'
      ],
      currentProjects: [
        'Quantum-enhanced portfolio optimization',
        'Quantum neural networks research',
        'Quantum key distribution protocols',
        'Variational quantum eigensolvers'
      ],
      publications: 12,
      patents: 5,
      collaborations: ['IBM Quantum', 'Microsoft Quantum', 'Rigetti Computing'],
      color: 'from-purple-400 to-pink-500'
    },
    {
      id: 'computer-vision',
      icon: <Eye className="w-12 h-12" />,
      title: 'Computer Vision & Robotics',
      description: 'Developing advanced computer vision systems and robotic applications for autonomous systems and human-machine interaction.',
      focus: [
        '3D scene understanding and reconstruction',
        'Real-time object detection and tracking',
        'Autonomous vehicle perception systems',
        'Medical imaging and diagnosis',
        'Robotic manipulation and control',
        'Augmented reality applications'
      ],
      currentProjects: [
        'Self-supervised learning for vision',
        'Multi-modal sensor fusion',
        'Human-robot collaboration systems',
        'Medical image analysis platforms'
      ],
      publications: 18,
      patents: 12,
      collaborations: ['NVIDIA Research', 'Carnegie Mellon Robotics', 'OpenAI'],
      color: 'from-green-400 to-teal-500'
    },
    {
      id: 'nlp',
      icon: <BookOpen className="w-12 h-12" />,
      title: 'Natural Language Processing',
      description: 'Pushing the boundaries of language understanding, generation, and multilingual AI systems.',
      focus: [
        'Multilingual and cross-lingual models',
        'Code generation and programming assistance',
        'Conversational AI and dialogue systems',
        'Information extraction and summarization',
        'Language model safety and bias mitigation',
        'Domain-specific language understanding'
      ],
      currentProjects: [
        'Low-resource language processing',
        'AI-powered code review systems',
        'Medical text understanding',
        'Real-time language translation'
      ],
      publications: 31,
      patents: 7,
      collaborations: ['Hugging Face', 'Allen Institute for AI', 'DeepMind'],
      color: 'from-orange-400 to-red-500'
    },
    {
      id: 'edge-computing',
      icon: <Cpu className="w-12 h-12" />,
      title: 'Edge Computing & IoT',
      description: 'Optimizing AI inference for edge devices and developing efficient IoT architectures for real-time applications.',
      focus: [
        'Model compression and quantization',
        'Federated learning at the edge',
        'Real-time inference optimization',
        'Energy-efficient AI hardware',
        'Distributed computing frameworks',
        'IoT security and privacy'
      ],
      currentProjects: [
        'TinyML for microcontrollers',
        'Edge AI for smart cities',
        'Industrial IoT monitoring systems',
        'Autonomous drone navigation'
      ],
      publications: 15,
      patents: 10,
      collaborations: ['Intel Labs', 'ARM Research', 'Qualcomm AI'],
      color: 'from-yellow-400 to-orange-500'
    },
    {
      id: 'cybersecurity',
      icon: <Shield className="w-12 h-12" />,
      title: 'AI-Powered Cybersecurity',
      description: 'Developing next-generation security systems using AI for threat detection, prevention, and response.',
      focus: [
        'Anomaly detection in network traffic',
        'AI-powered threat intelligence',
        'Behavioral analysis for insider threats',
        'Automated incident response',
        'Privacy-preserving security analytics',
        'Adversarial machine learning defense'
      ],
      currentProjects: [
        'Zero-day exploit detection',
        'AI security for IoT devices',
        'Blockchain-based identity management',
        'Quantum-safe cryptography'
      ],
      publications: 21,
      patents: 14,
      collaborations: ['NIST', 'DARPA', 'Cybersecurity Coalition'],
      color: 'from-red-400 to-pink-500'
    }
  ];

  const researchPapers = [
    {
      title: "Federated Learning with Differential Privacy: A Comprehensive Survey",
      authors: ["Dr. Sarah Chen", "Dr. Michael Rodriguez", "Dr. Emily Johnson"],
      journal: "Nature Machine Intelligence",
      year: 2024,
      citations: 156,
      category: "ai-ml",
      abstract: "This paper provides a comprehensive survey of federated learning techniques that preserve privacy through differential privacy mechanisms.",
      tags: ["Federated Learning", "Privacy", "Differential Privacy", "Machine Learning"],
      status: "Published",
      impact: "High"
    },
    {
      title: "Quantum-Enhanced Portfolio Optimization for Financial Markets",
      authors: ["Dr. David Kim", "Dr. Lisa Wong"],
      journal: "Quantum Information Processing",
      year: 2024,
      citations: 89,
      category: "quantum",
      abstract: "We present a novel quantum algorithm for portfolio optimization that demonstrates quadratic speedup over classical methods.",
      tags: ["Quantum Computing", "Portfolio Optimization", "Finance", "QAOA"],
      status: "Published",
      impact: "Medium"
    },
    {
      title: "Real-time 3D Object Detection in Autonomous Vehicles Using Multi-modal Fusion",
      authors: ["Dr. Emily Johnson", "Dr. Michael Rodriguez"],
      journal: "IEEE Transactions on Robotics",
      year: 2024,
      citations: 234,
      category: "computer-vision",
      abstract: "A novel approach for real-time 3D object detection combining LiDAR, camera, and radar data for autonomous driving applications.",
      tags: ["Computer Vision", "Autonomous Vehicles", "3D Detection", "Sensor Fusion"],
      status: "Published",
      impact: "High"
    },
    {
      title: "Large Language Models for Code Generation: A Comparative Study",
      authors: ["Dr. Sarah Chen", "Development Team"],
      journal: "ACL 2024",
      year: 2024,
      citations: 312,
      category: "nlp",
      abstract: "Comprehensive evaluation of large language models for code generation across multiple programming languages and domains.",
      tags: ["NLP", "Code Generation", "LLM", "Programming"],
      status: "Published",
      impact: "Very High"
    },
    {
      title: "TinyML: Efficient Neural Networks for Edge Devices",
      authors: ["Dr. David Kim", "Hardware Team"],
      journal: "IEEE Computer",
      year: 2024,
      citations: 178,
      category: "edge-computing",
      abstract: "Novel techniques for deploying neural networks on resource-constrained edge devices with minimal accuracy loss.",
      tags: ["Edge Computing", "TinyML", "Neural Networks", "Optimization"],
      status: "Published",
      impact: "High"
    },
    {
      title: "AI-Driven Anomaly Detection in Industrial IoT Networks",
      authors: ["Dr. Lisa Wong", "Dr. Michael Rodriguez"],
      journal: "IEEE Internet of Things Journal",
      year: 2025,
      citations: 45,
      category: "cybersecurity",
      abstract: "A comprehensive framework for detecting anomalies in industrial IoT networks using advanced machine learning techniques.",
      tags: ["Cybersecurity", "IoT", "Anomaly Detection", "Industrial Systems"],
      status: "Under Review",
      impact: "Medium"
    }
  ];

  const researchStats = [
    { icon: <BookOpen className="w-8 h-8" />, value: '120+', label: 'Research Papers' },
    { icon: <Award className="w-8 h-8" />, value: '56', label: 'Patents Filed' },
    { icon: <Users className="w-8 h-8" />, value: '25+', label: 'Research Partners' },
    { icon: <Target className="w-8 h-8" />, value: '15', label: 'Active Projects' }
  ];

  const upcomingEvents = [
    {
      date: '2024-03-15',
      title: 'AI Research Symposium 2024',
      location: 'San Francisco, CA',
      type: 'Conference',
      description: 'Presenting our latest research on federated learning and edge AI optimization.'
    },
    {
      date: '2024-04-22',
      title: 'Quantum Computing Workshop',
      location: 'MIT, Cambridge',
      type: 'Workshop',
      description: 'Hands-on workshop on quantum machine learning algorithms and applications.'
    },
    {
      date: '2024-05-10',
      title: 'Computer Vision Summit',
      location: 'Virtual Event',
      type: 'Keynote',
      description: 'Keynote on "The Future of Autonomous Systems and Computer Vision".'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(0, 245, 255, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(123, 44, 191, 0.15) 0%, transparent 50%)
              `,
            }}
          />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-6">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-semibold">Advancing the Future of AI</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Research &
              </span>
              <br />
              <span className="text-white">Innovation Lab</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-12">
              Pushing the boundaries of artificial intelligence, quantum computing, and emerging technologies 
              to shape the future of digital transformation.
            </p>

            {/* Research Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {researchStats.map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.6 }}
                  className="bg-gradient-to-br from-cyan-500/10 to-purple-600/10 p-6 rounded-2xl border border-cyan-400/30"
                >
                  <div className="text-cyan-400 mb-3 flex justify-center">{stat.icon}</div>
                  <div className="text-3xl font-black text-white mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research Areas */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Research Focus Areas
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our multidisciplinary research spans cutting-edge domains, driving innovation 
              at the intersection of AI, quantum computing, and advanced computing systems.
            </p>
          </motion.div>

          {/* Research Area Selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {researchAreas.map((area, idx) => (
              <motion.button
                key={area.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedResearchArea(idx)}
                className={`flex items-center gap-3 px-6 py-3 rounded-full font-semibold transition-all ${
                  selectedResearchArea === idx
                    ? 'bg-gradient-to-r from-cyan-400 to-purple-600 text-white shadow-lg shadow-cyan-400/50'
                    : 'bg-gray-800/50 text-gray-300 hover:text-cyan-400 border border-gray-700 hover:border-cyan-400/50'
                }`}
              >
                <div className="w-6 h-6">{React.cloneElement(area.icon, { className: "w-6 h-6" })}</div>
                <span className="hidden sm:inline text-sm">{area.title.split(' ')[0]}</span>
              </motion.button>
            ))}
          </div>

          {/* Selected Research Area Details */}
          <motion.div
            key={selectedResearchArea}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-gray-900 to-black rounded-3xl border border-cyan-400/20 p-8 md:p-12"
          >
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-cyan-400">
                    {researchAreas[selectedResearchArea].icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-white">
                    {researchAreas[selectedResearchArea].title}
                  </h3>
                </div>
                
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  {researchAreas[selectedResearchArea].description}
                </p>

                {/* Research Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="bg-cyan-500/10 p-4 rounded-xl border border-cyan-400/30 text-center">
                    <div className="text-2xl font-black text-cyan-400 mb-1">
                      {researchAreas[selectedResearchArea].publications}
                    </div>
                    <div className="text-sm text-gray-400">Publications</div>
                  </div>
                  <div className="bg-purple-500/10 p-4 rounded-xl border border-purple-400/30 text-center">
                    <div className="text-2xl font-black text-purple-400 mb-1">
                      {researchAreas[selectedResearchArea].patents}
                    </div>
                    <div className="text-sm text-gray-400">Patents</div>
                  </div>
                  <div className="bg-green-500/10 p-4 rounded-xl border border-green-400/30 text-center">
                    <div className="text-2xl font-black text-green-400 mb-1">
                      {researchAreas[selectedResearchArea].collaborations.length}
                    </div>
                    <div className="text-sm text-gray-400">Partners</div>
                  </div>
                </div>

                {/* Collaborations */}
                <div>
                  <h4 className="text-lg font-bold text-white mb-4">Research Collaborations</h4>
                  <div className="flex flex-wrap gap-2">
                    {researchAreas[selectedResearchArea].collaborations.map((partner, idx) => (
                      <span
                        key={idx}
                        className="bg-gradient-to-r from-cyan-500/20 to-purple-600/20 text-cyan-300 px-3 py-1 rounded-full text-sm border border-cyan-400/30"
                      >
                        {partner}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                {/* Research Focus */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Microscope className="w-6 h-6 text-cyan-400" />
                    Research Focus
                  </h4>
                  <div className="space-y-3">
                    {researchAreas[selectedResearchArea].focus.map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg"
                      >
                        <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Current Projects */}
                <div>
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Lightbulb className="w-6 h-6 text-cyan-400" />
                    Active Projects
                  </h4>
                  <div className="space-y-3">
                    {researchAreas[selectedResearchArea].currentProjects.map((project, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-start gap-3 p-3 bg-gradient-to-r from-cyan-500/10 to-purple-600/10 rounded-lg border border-cyan-400/30"
                      >
                        <Target className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{project}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Recent Publications */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Recent Publications
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Latest research papers and publications from our team, advancing the state of the art 
              in AI and emerging technologies.
            </p>
          </motion.div>

          <div className="grid gap-8">
            {researchPapers.map((paper, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                onHoverStart={() => setHoveredPaper(idx)}
                onHoverEnd={() => setHoveredPaper(null)}
                className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl border border-gray-700 hover:border-cyan-400/50 transition-all group"
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-grow">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                          {paper.title}
                        </h3>
                        <p className="text-cyan-400 text-sm mb-2">
                          {paper.authors.join(', ')}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>{paper.journal}</span>
                          <span>•</span>
                          <span>{paper.year}</span>
                          <span>•</span>
                          <span>{paper.citations} citations</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          paper.status === 'Published' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {paper.status}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          paper.impact === 'Very High' ? 'bg-red-500/20 text-red-400' :
                          paper.impact === 'High' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {paper.impact} Impact
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-300 mb-4 leading-relaxed">
                      {paper.abstract}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {paper.tags.map((tag, tagIdx) => (
                        <span
                          key={tagIdx}
                          className="bg-gray-700/50 text-gray-300 px-2 py-1 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <motion.div
                      initial={false}
                      animate={{ opacity: hoveredPaper === idx ? 1 : 0 }}
                      className="flex items-center gap-4"
                    >
                      <button className="text-cyan-400 font-semibold flex items-center gap-2 hover:text-cyan-300 transition-colors">
                        <Download className="w-4 h-4" />
                        Download PDF
                      </button>
                      <button className="text-cyan-400 font-semibold flex items-center gap-2 hover:text-cyan-300 transition-colors">
                        <Globe className="w-4 h-4" />
                        View Online
                      </button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-cyan-400 to-purple-600 px-8 py-4 rounded-full font-bold text-white hover:shadow-xl hover:shadow-cyan-400/50 transition-all flex items-center gap-2 mx-auto"
            >
              View All Publications
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Upcoming Events
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Join us at upcoming conferences, workshops, and research presentations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {upcomingEvents.map((event, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-cyan-500/10 to-purple-600/10 p-8 rounded-3xl border border-cyan-400/30 hover:border-cyan-400/60 transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-8 h-8 text-cyan-400" />
                  <div>
                    <div className="text-cyan-400 font-bold">
                      {new Date(event.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </div>
                    <div className="text-sm text-gray-400">{event.type}</div>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3">{event.title}</h3>
                <p className="text-cyan-400 text-sm mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {event.location}
                </p>
                <p className="text-gray-300 text-sm leading-relaxed">{event.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-500/10 to-purple-600/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              Collaborate with Our Research Team
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Interested in collaborating on cutting-edge research or applying our innovations 
              to real-world challenges? Let's explore partnership opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-400 to-purple-600 px-8 py-4 rounded-full font-bold text-white hover:shadow-xl hover:shadow-cyan-400/50 transition-all flex items-center gap-2 justify-center"
              >
                Start Collaboration
                <ArrowRight className="w-5 h-5" />
              </motion.a>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-cyan-400 text-cyan-400 px-8 py-4 rounded-full font-bold hover:bg-cyan-400 hover:text-black transition-all"
              >
                Research Newsletter
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResearchPage;